/**
 * Showing a compatibility score.
 *
 * One presentational decision is baked in here and worth stating plainly:
 * the scale is *not* stretched. A score is the weighted mean agreement across
 * everything two people both answered, which means two strangers with no
 * relationship to each other still land in the middle of the scale rather
 * than at the bottom of it.
 *
 * Rather than rescaling the number into something that looks more dramatic,
 * the bar marks where "no better than chance" sits, so 56% reads as ordinary
 * and 80% reads as remarkable, which is what they respectively are.
 */

import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { SCALE_MAX } from "@/shared/scale";
import type { GroupLeaning, GroupScore } from "@/shared/types";

/**
 * Where two randomly-drawn people land, measured against the seeded pool.
 *
 * The mean score over 40k random pairs generated the way seedDemoProfiles()
 * generates them. It moves whenever the scoring rule does: under the old
 * linear similarity this was 0.61, and the distance table in shared/scale
 * pulled it down to 0.55 — unrelated people disagree by two or three stops on
 * most questions, and that band is exactly where the table stopped paying out.
 *
 * Per-group scores sit within a point of the same number (0.54), so one line
 * serves both the headline bar and the breakdown bars.
 */
export const CHANCE_BASELINE = 0.55;

/**
 * The baseline as a percentage, formatted once.
 *
 * Every place that mentions the number reads it from here, so the tick on the
 * bar, its tooltip and the sentence under the headline can't come to disagree
 * about what "average" is after someone edits the constant above.
 */
export const CHANCE_LABEL = `${Math.round(CHANCE_BASELINE * 100)}%`;

const CHANCE_TITLE = `Average match: two people picked at random score about ${CHANCE_LABEL}`;

export function scoreTone(score: number): string {
  if (score >= 0.78) return "text-brand";
  if (score >= CHANCE_BASELINE + 0.04) return "text-foreground";
  return "text-muted-foreground";
}

export function scoreWord(score: number): string {
  if (score >= 0.82) return "Exceptional";
  if (score >= 0.75) return "Strong";
  if (score >= 0.68) return "Good";
  if (score >= CHANCE_BASELINE + 0.03) return "Slightly above average";
  if (score >= CHANCE_BASELINE - 0.03) return "About average";
  return "Below average";
}

/** How far a score sits from average, in plain percentage points. */
function comparedToAverage(score: number): string {
  const points = Math.round(score * 100) - Math.round(CHANCE_BASELINE * 100);
  if (points === 0) return "right on it";
  const word = Math.abs(points) === 1 ? "point" : "points";
  return `${Math.abs(points)} ${word} ${points > 0 ? "above" : "below"} it`;
}

export function ScoreBar({
  score,
  className,
  showBaseline = true,
}: {
  score: number;
  className?: string;
  showBaseline?: boolean;
}) {
  return (
    // No overflow-hidden: the marker deliberately stands proud of the track,
    // and the fill is rounded to the same radius so nothing needs clipping.
    <div className={cn("relative h-1.5 rounded-full bg-muted", className)}>
      <div
        className="h-full rounded-full bg-gradient-to-r from-brand to-brand-alt transition-[width] duration-500"
        style={{ width: `${Math.max(1.5, score * 100)}%` }}
      />
      {showBaseline && (
        // Centred on the baseline rather than starting at it, and ringed in
        // the page background so it stays legible where the fill runs past it.
        <div
          aria-hidden
          title={CHANCE_TITLE}
          className="absolute -inset-y-1 w-0.75 -translate-x-1/2 rounded-full bg-foreground/45 ring-1 ring-background/80"
          style={{ left: `${CHANCE_BASELINE * 100}%` }}
        />
      )}
    </div>
  );
}

/**
 * Names the marker for lists of bars that have no room for a caption.
 *
 * Draws a miniature of the tick alongside the number, so there's no guessing
 * which mark on the bar the sentence is talking about.
 */
export function ChanceLegend({ className }: { className?: string }) {
  return (
    <p className={cn("flex items-center gap-2 text-[12.5px] text-muted-foreground", className)}>
      <span
        aria-hidden
        className="inline-block h-3 w-0.75 shrink-0 rounded-full bg-foreground/45"
      />
      Average match {CHANCE_LABEL} — the marker on each bar.
    </p>
  );
}

/** The headline number. */
export function TotalScore({ score }: { score: number }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-card/50 p-5 backdrop-blur-sm">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[12px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
            Overall
          </div>
          <div className="mt-1 text-4xl font-semibold tracking-[-0.03em] text-gradient">
            {Math.round(score * 100)}%
          </div>
        </div>
        <div className={cn("pb-1.5 text-[13.5px] font-medium", scoreTone(score))}>
          {scoreWord(score)}
        </div>
      </div>
      <ScoreBar score={score} className="mt-3.5 h-2" />
      <p className="mt-2.5 text-[12px] leading-relaxed text-muted-foreground">
        <span className="font-medium text-foreground/70">
          Average match {CHANCE_LABEL}
        </span>{" "}
        — that's the marker, where two people picked at random tend to land. This
        pair is {comparedToAverage(score)}.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Your own leanings — a different measurement, drawn differently on purpose
// ---------------------------------------------------------------------------

/**
 * The average of one person's own answers in one area.
 *
 * Drawn from the centre out, like the sliders that produced it, rather than
 * filled from the left like a score. A score is "how much of the maximum",
 * where left is bad; this is a *position*, where left means one thing and
 * right means another and the middle means neither. Reusing the score bar
 * would say 50% for a perfectly ordinary "no strong feelings either way".
 */
function LeaningBar({ average }: { average: number }) {
  const pos = ((average + SCALE_MAX) / (SCALE_MAX * 2)) * 100;
  const color =
    Math.abs(average) < 0.05
      ? "var(--muted-foreground)"
      : average < 0
        ? "var(--negative)"
        : "var(--positive)";

  return (
    <div className="relative h-1.5 rounded-full bg-muted">
      <div
        className="absolute top-0 h-full rounded-full transition-[width,left] duration-500"
        style={{
          left: `${Math.min(50, pos)}%`,
          width: `${Math.max(1.5, Math.abs(pos - 50))}%`,
          background: color,
          opacity: 0.85,
        }}
      />
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 h-2.5 w-px -translate-x-1/2 -translate-y-1/2 bg-border"
      />
    </div>
  );
}

/** Signed to one decimal, so "+1.4" and "−0.6" read as positions on the scale. */
function leaningNumber(average: number): string {
  const rounded = Math.round(average * 10) / 10;
  if (Math.abs(rounded) < 0.05) return "0";
  // A real minus sign, to match the −3 the rest of the product shows.
  return rounded > 0 ? `+${rounded.toFixed(1)}` : `−${Math.abs(rounded).toFixed(1)}`;
}

/**
 * Per-area averages for the person they belong to.
 *
 * Only ever rendered on your own public page — see GroupLeaning in
 * shared/types for why this can't be shown to anybody else.
 */
export function GroupLeanings({
  groups,
  className,
}: {
  groups: GroupLeaning[];
  className?: string;
}) {
  const answered = groups.filter((g) => g.answered > 0);
  if (answered.length === 0) {
    return (
      <p className={cn("text-[14px] text-muted-foreground", className)}>
        Nothing answered yet, so there's nothing to average.
      </p>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {answered.map((g) => (
        <div key={g.groupKey}>
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <span className="text-[14px]">
              <span aria-hidden className="mr-1.5">
                {g.emoji}
              </span>
              {g.groupLabel}
            </span>
            <span className="shrink-0 text-[13.5px] font-medium tabular-nums">
              {leaningNumber(g.average)}
              <span className="ml-1.5 text-[11.5px] font-normal text-muted-foreground/70">
                {g.answered}/{g.total}
              </span>
            </span>
          </div>
          <LeaningBar average={g.average} />
        </div>
      ))}
    </div>
  );
}

/**
 * One area's bar: emoji, label, percentage, count.
 *
 * Extracted because two things draw it now — the plain breakdown below, and
 * `AnswerCompare`, where the same row becomes the lid on the answers that
 * produced the number. Passing `onToggle` is what turns it into a disclosure;
 * without one it renders exactly as it always has, so the page that has no
 * answers to show is not paying for the page that does.
 */
export function GroupScoreRow({
  group,
  open,
  onToggle,
  children,
}: {
  group: GroupScore;
  open?: boolean;
  onToggle?: () => void;
  children?: React.ReactNode;
}) {
  const score = group.score ?? 0;

  const head = (
    <>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-baseline gap-1.5 text-[14px]">
          {onToggle && (
            <ChevronRight
              aria-hidden
              className={cn(
                "size-3.5 shrink-0 self-center text-muted-foreground/60 transition-transform",
                open && "rotate-90",
              )}
            />
          )}
          <span aria-hidden>{group.emoji}</span>
          <span className="truncate">{group.groupLabel}</span>
        </span>
        <span className={cn("shrink-0 text-[13.5px] font-medium tabular-nums", scoreTone(score))}>
          {Math.round(score * 100)}%
          <span className="ml-1.5 text-[11.5px] font-normal text-muted-foreground/70">
            {group.overlap}q
          </span>
        </span>
      </div>
      <ScoreBar score={score} />
    </>
  );

  if (!onToggle) return <div>{head}</div>;

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        // -mx/px so the hover tint bleeds to the card edge rather than sitting
        // inset from it, and py to bring a 30px-tall row up to a 44px target.
        className="-mx-2 w-full rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-accent/50"
      >
        {head}
      </button>
      {open && children}
    </div>
  );
}

/** Per-group breakdown. Percentages only — never the answers behind them. */
export function GroupScores({
  groups,
  className,
}: {
  groups: GroupScore[];
  className?: string;
}) {
  const scored = groups.filter((g) => g.score !== null);
  if (scored.length === 0) {
    return (
      <p className={cn("text-[14px] text-muted-foreground", className)}>
        Not enough overlapping answers yet to break this down.
      </p>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {scored.map((g) => (
        <GroupScoreRow key={g.groupKey} group={g} />
      ))}
    </div>
  );
}
